import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { EChartsOption } from 'echarts';
import type { ClientDto, ConsultingEvent, ProjectDto, TeamMemberDto } from '@jordan/types';
import { Alert, Breadcrumb, Button, EChart, PageHeader, SelectMenu, Spinner } from '@jordan/ui';
import { AppChrome } from '../../components/AppChrome';
import { ApiError, apiGet } from '../../lib/api';
import { billingTimeSidebar } from './billingTimeSidebar';

const DAY_MS = 86_400_000;
const PALETTE = ['#a855f7', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#6366f1', '#ec4899', '#14b8a6'];

type Dim = 'BILLABLE' | 'MEMBERS' | 'CLIENTS' | 'PROJECTS' | 'TASKS' | 'TAGS';
const DIMS: { value: Dim; label: string; distribution: string }[] = [
  { value: 'BILLABLE', label: 'Billable', distribution: 'Billable distribution' },
  { value: 'MEMBERS', label: 'Members', distribution: 'Member distribution' },
  { value: 'CLIENTS', label: 'Clients', distribution: 'Client distribution' },
  { value: 'PROJECTS', label: 'Projects', distribution: 'Project distribution' },
  { value: 'TASKS', label: 'Tasks', distribution: 'Task distribution' },
  { value: 'TAGS', label: 'Tags', distribution: 'Tag distribution' },
];

const TABS = ['Summary', 'Detailed', 'Workload', 'Profitability', 'My reports'] as const;

// ---- date / format helpers (local) ----
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
function mondayOf(d: Date): Date {
  const s = startOfDay(d);
  s.setDate(s.getDate() - ((s.getDay() + 6) % 7));
  return s;
}
const clock = (hours: number) => {
  const s = Math.round(hours * 3600);
  return `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};
const shortDay = (d: Date) => d.toLocaleDateString(undefined, { month: '2-digit', day: '2-digit' });
// Billed time is the source of truth when set; otherwise use the calendar appointment's length.
const durHours = (e: ConsultingEvent) =>
  e.billedMinutes != null
    ? Math.max(0, e.billedMinutes) / 60
    : Math.max(0, (new Date(e.end).getTime() - new Date(e.start).getTime()) / 3_600_000);
// `amount` is the per-hour billing rate set on the entry; the entry's value is rate × hours.
const entryAmount = (e: ConsultingEvent) => (e.billable && e.amount != null ? e.amount * durHours(e) : 0);
const money = (n: number) => new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(n);

/** Stable colour per bucket: billable/non-billable get fixed hues; others cycle the palette. */
function colorFor(buckets: string[]) {
  const m = new Map(
    buckets.map((b, i) => [b, b === 'Non-billable' ? '#a855f7' : b === 'Billable' ? '#0ea5e9' : PALETTE[i % PALETTE.length]]),
  );
  return (b: string) => m.get(b) ?? '#94a3b8';
}

type Preset = 'this-week' | 'last-week' | 'this-month' | 'last-30d';
function presetRange(p: Preset): { from: Date; to: Date } {
  const today = startOfDay(new Date());
  if (p === 'this-week') return { from: mondayOf(today), to: new Date(mondayOf(today).getTime() + 6 * DAY_MS) };
  if (p === 'last-week') {
    const from = new Date(mondayOf(today).getTime() - 7 * DAY_MS);
    return { from, to: new Date(from.getTime() + 6 * DAY_MS) };
  }
  if (p === 'this-month') return { from: new Date(today.getFullYear(), today.getMonth(), 1), to: new Date(today.getFullYear(), today.getMonth() + 1, 0) };
  return { from: new Date(today.getTime() - 29 * DAY_MS), to: today };
}

export function ReportsPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [preset, setPreset] = useState<Preset>('this-week');
  const [range, setRange] = useState(() => presetRange('this-week'));
  const [events, setEvents] = useState<ConsultingEvent[] | null>(null);
  const [clients, setClients] = useState<ClientDto[]>([]);
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [team, setTeam] = useState<TeamMemberDto[]>([]);
  const [notConfigured, setNotConfigured] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const projectById = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);
  const memberById = useMemo(() => new Map(team.map((m) => [m.id, m])), [team]);

  const [memberFilter, setMemberFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  // Default to billable only — non-billable time is hidden until "All events" is chosen.
  const [billableFilter, setBillableFilter] = useState<'all' | 'billable' | 'nonbillable'>('billable');
  const [stackBy, setStackBy] = useState<Dim>('BILLABLE');
  const [sliceBy, setSliceBy] = useState<Dim>('PROJECTS');
  const [breakdownBy, setBreakdownBy] = useState<Dim>('PROJECTS');

  useEffect(() => {
    apiGet<ClientDto[]>('/api/consulting/clients').then(setClients).catch(() => undefined);
    apiGet<ProjectDto[]>('/api/consulting/projects').then(setProjects).catch(() => undefined);
    apiGet<TeamMemberDto[]>('/api/consulting/team').then(setTeam).catch(() => undefined);
  }, []);

  useEffect(() => {
    setEvents(null);
    setErr(null);
    setNotConfigured(false);
    const from = startOfDay(range.from).toISOString();
    const to = new Date(startOfDay(range.to).getTime() + DAY_MS).toISOString();
    apiGet<ConsultingEvent[]>(`/api/consulting/events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`)
      .then(setEvents)
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 409) setNotConfigured(true);
        else setErr(e instanceof Error ? e.message : 'Could not load report data.');
      });
  }, [range]);

  const applyPreset = (p: Preset) => {
    setPreset(p);
    setRange(presetRange(p));
  };
  const shift = (dir: 1 | -1) => {
    setRange((r) => {
      const span = Math.round((startOfDay(r.to).getTime() - startOfDay(r.from).getTime()) / DAY_MS) + 1;
      return { from: new Date(r.from.getTime() + dir * span * DAY_MS), to: new Date(r.to.getTime() + dir * span * DAY_MS) };
    });
  };

  // Timed events in range, after the member/client/project filters. Events carry no client/project
  // link yet, so selecting a specific one yields no rows (everything is "Without project").
  const filtered = useMemo(() => {
    return (events ?? [])
      .filter((e) => !e.allDay)
      .filter((e) => {
        if (billableFilter === 'billable' && !e.billable) return false;
        if (billableFilter === 'nonbillable' && e.billable) return false;
        if (memberFilter !== 'all' && e.memberId !== memberFilter) return false;
        if (projectFilter !== 'all' && e.projectId !== projectFilter) return false;
        if (clientFilter !== 'all') {
          const clientId = e.projectId ? (projectById.get(e.projectId)?.clientId ?? null) : null;
          if (clientId !== clientFilter) return false;
        }
        return true;
      });
  }, [events, billableFilter, memberFilter, clientFilter, projectFilter, projectById]);

  const bucketOf = useCallback(
    (e: ConsultingEvent, dim: Dim): string => {
      switch (dim) {
        case 'BILLABLE':
          return e.billable ? 'Billable' : 'Non-billable';
        case 'MEMBERS':
          return e.memberId ? (memberById.get(e.memberId)?.name ?? 'Unknown member') : 'No member';
        case 'CLIENTS': {
          const p = e.projectId ? projectById.get(e.projectId) : undefined;
          return p?.clientName ?? 'Without client';
        }
        case 'PROJECTS':
          return e.projectId ? (projectById.get(e.projectId)?.name ?? 'Unknown project') : 'Without project';
        case 'TASKS':
          return 'No task';
        case 'TAGS':
          return 'No tag';
      }
    },
    [memberById, projectById],
  );

  // ---- aggregates ----
  const totalHours = filtered.reduce((s, e) => s + durHours(e), 0);
  const billableHours = filtered.reduce((s, e) => s + (e.billable ? durHours(e) : 0), 0);
  const totalAmount = filtered.reduce((s, e) => s + entryAmount(e), 0);
  const activeDays = new Set(filtered.map((e) => ymd(startOfDay(new Date(e.start))))).size;
  const avgDaily = activeDays ? totalHours / activeDays : 0;

  const days = useMemo(() => {
    const out: Date[] = [];
    for (let t = startOfDay(range.from).getTime(); t <= startOfDay(range.to).getTime(); t += DAY_MS) out.push(new Date(t));
    return out;
  }, [range]);

  const barOption = useMemo<EChartsOption>(() => {
    const buckets = [...new Set(filtered.map((e) => bucketOf(e, stackBy)))];
    const color = colorFor(buckets);
    const perDay = (bucket: string) =>
      days.map((d) => {
        const key = ymd(d);
        return filtered
          .filter((e) => ymd(startOfDay(new Date(e.start))) === key && bucketOf(e, stackBy) === bucket)
          .reduce((s, e) => s + durHours(e), 0);
      });
    return {
      textStyle: { fontFamily: 'var(--body-font-family)', color: 'var(--body-color)' },
      // Reserve room at the bottom for the legend so it clears the x-axis labels.
      grid: { left: 8, right: 16, top: 16, bottom: 44, containLabel: true },
      legend: { bottom: 0, textStyle: { color: 'var(--body-color)' } },
      tooltip: { trigger: 'axis', valueFormatter: (v) => clock(Number(v)) },
      xAxis: {
        type: 'category',
        data: days.map((d) => `${d.toLocaleDateString(undefined, { weekday: 'short' })} ${shortDay(d)}`),
        axisLabel: { color: 'rgba(var(--body-color-rgb), .65)' },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLabel: { color: 'rgba(var(--body-color-rgb), .55)', formatter: (v: number) => `${v}h` },
        splitLine: { lineStyle: { color: 'var(--gray-200)', type: 'dashed' } },
      },
      series: buckets.map((b) => ({
        name: b,
        type: 'bar',
        stack: 'total',
        barMaxWidth: 36,
        itemStyle: { color: color(b), borderRadius: [3, 3, 0, 0] },
        data: perDay(b),
      })),
    } as EChartsOption;
  }, [filtered, days, stackBy, bucketOf]);

  const donutData = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of filtered) map.set(bucketOf(e, sliceBy), (map.get(bucketOf(e, sliceBy)) ?? 0) + durHours(e));
    return [...map.entries()].map(([name, value]) => ({ name, value }));
  }, [filtered, sliceBy, bucketOf]);

  const donutOption = useMemo<EChartsOption>(() => {
    const color = colorFor(donutData.map((d) => d.name));
    return {
      textStyle: { fontFamily: 'var(--body-font-family)', color: 'var(--body-color)' },
      tooltip: { trigger: 'item', valueFormatter: (v) => clock(Number(v)) },
      legend: { bottom: 0, textStyle: { color: 'var(--body-color)' } },
      series: [
        {
          type: 'pie',
          radius: ['55%', '78%'],
          center: ['50%', '46%'],
          avoidLabelOverlap: true,
          label: { show: true, position: 'center', formatter: () => clock(totalHours), color: 'var(--body-color)', fontSize: 18, fontWeight: 'bold' },
          data: donutData.map((d) => ({ name: d.name, value: d.value, itemStyle: { color: color(d.name) } })),
        },
      ],
    } as EChartsOption;
  }, [donutData, totalHours]);

  const breakdownRows = useMemo(() => {
    const map = new Map<string, { hours: number; amount: number }>();
    for (const e of filtered) {
      const k = bucketOf(e, breakdownBy);
      const cur = map.get(k) ?? { hours: 0, amount: 0 };
      cur.hours += durHours(e);
      cur.amount += entryAmount(e);
      map.set(k, cur);
    }
    return [...map.entries()]
      .map(([label, v]) => ({ label, hours: v.hours, amount: v.amount, pct: totalHours ? (v.hours / totalHours) * 100 : 0 }))
      .sort((a, b) => b.hours - a.hours);
  }, [filtered, breakdownBy, totalHours, bucketOf]);

  const rangeLabel = `${range.from.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${range.to.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  const dimOptions = DIMS.map((d) => ({ value: d.value, label: d.label }));
  const distributionTitle = DIMS.find((d) => d.value === sliceBy)?.distribution ?? 'Distribution';

  return (
    <AppChrome secondarySidebar={billingTimeSidebar(navigate, pathname)}>
      <PageHeader
        title={
          <>
            Billing &amp; Time - <span className="fw-normal">Reports</span>
          </>
        }
        breadcrumb={
          <Breadcrumb
            items={[
              { icon: 'ph-house', href: '/' },
              { label: 'Consulting' },
              { label: 'Billing & Time' },
              { label: 'Reports' },
            ]}
          />
        }
      />

      <div className="content">
        {/* Report tabs */}
        <ul className="nav nav-tabs mb-3">
          {TABS.map((t) => (
            <li className="nav-item" key={t}>
              <span
                className={`nav-link ${t === 'Summary' ? 'active' : 'disabled text-muted'}`}
                title={t === 'Summary' ? undefined : 'Coming soon'}
              >
                {t}
              </span>
            </li>
          ))}
        </ul>

        {/* Filters */}
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <div className="btn-group">
            <Button variant="light" icon="ph-caret-left" onClick={() => shift(-1)} />
            <Button variant="light" icon="ph-caret-right" onClick={() => shift(1)} />
          </div>
          <div style={{ minWidth: 170 }}>
            <SelectMenu
              className="mb-0"
              icon="ph-calendar-blank"
              value={preset}
              onChange={(v) => applyPreset(v as Preset)}
              options={[
                { value: 'this-week', label: 'This week' },
                { value: 'last-week', label: 'Last week' },
                { value: 'this-month', label: 'This month' },
                { value: 'last-30d', label: 'Last 30 days' },
              ]}
            />
          </div>
          <span className="text-muted me-auto">{rangeLabel}</span>
          <div style={{ minWidth: 150 }}>
            <SelectMenu
              className="mb-0"
              icon="ph-currency-dollar"
              value={billableFilter}
              onChange={(v) => setBillableFilter(v as 'all' | 'billable' | 'nonbillable')}
              options={[
                { value: 'all', label: 'All events' },
                { value: 'billable', label: 'Billable' },
                { value: 'nonbillable', label: 'Non-billable' },
              ]}
            />
          </div>
          <div style={{ minWidth: 150 }}>
            <SelectMenu className="mb-0" icon="ph-user" value={memberFilter} onChange={setMemberFilter} options={[{ value: 'all', label: 'All members' }, ...team.map((m) => ({ value: m.id, label: m.name }))]} />
          </div>
          <div style={{ minWidth: 150 }}>
            <SelectMenu className="mb-0" icon="ph-identification-card" value={clientFilter} onChange={setClientFilter} options={[{ value: 'all', label: 'All clients' }, ...clients.map((c) => ({ value: c.id, label: c.name }))]} />
          </div>
          <div style={{ minWidth: 150 }}>
            <SelectMenu className="mb-0" icon="ph-folders" value={projectFilter} onChange={setProjectFilter} options={[{ value: 'all', label: 'All projects' }, ...projects.map((p) => ({ value: p.id, label: p.name }))]} />
          </div>
        </div>

        {err && (
          <Alert variant="warning" icon="ph-warning" dismissible>
            {err}
          </Alert>
        )}

        {notConfigured ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5">
              <i className="ph-calendar-x d-block fs-1 text-muted mb-2" />
              <h5 className="mb-1">No calendar configured</h5>
              <p className="text-muted mb-3">Reports read from the calendar this app tracks time against.</p>
              <Button variant="primary" icon="ph-gear" onClick={() => navigate('/configuration/consulting')}>
                Configure calendar
              </Button>
            </div>
          </div>
        ) : events === null ? (
          <Spinner />
        ) : (
          <>
            {/* Summary cards */}
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-body">
                <div className="row text-center text-md-start">
                  <SummaryStat label="Total Hours" value={clock(totalHours)} />
                  <SummaryStat label="Billable Hours" value={billableHours > 0 ? clock(billableHours) : '–'} />
                  <SummaryStat label="Amount" value={totalAmount > 0 ? money(totalAmount) : '–'} />
                  <SummaryStat label="Average Daily Hours" value={`${avgDaily.toFixed(2)} Hours`} />
                </div>
              </div>
            </div>

            <div className="row g-3 mb-3">
              {/* Duration by day */}
              <div className="col-xl-8">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-transparent d-flex align-items-center">
                    <h6 className="mb-0 me-auto fw-semibold">Duration by day</h6>
                    <div style={{ minWidth: 170 }}>
                      <SelectMenu className="mb-0" icon="ph-stack" value={stackBy} onChange={(v) => setStackBy(v as Dim)} options={dimOptions} />
                    </div>
                  </div>
                  <div className="card-body">
                    <EChart option={barOption} height={340} />
                  </div>
                </div>
              </div>
              {/* Distribution */}
              <div className="col-xl-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-transparent d-flex align-items-center">
                    <h6 className="mb-0 me-auto fw-semibold">{distributionTitle}</h6>
                    <div style={{ minWidth: 150 }}>
                      <SelectMenu className="mb-0" icon="ph-chart-pie" value={sliceBy} onChange={(v) => setSliceBy(v as Dim)} options={dimOptions} />
                    </div>
                  </div>
                  <div className="card-body">
                    {donutData.length === 0 ? (
                      <div className="text-center text-muted py-5">No data for this range.</div>
                    ) : (
                      <EChart option={donutOption} height={340} />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Breakdown table */}
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-transparent d-flex align-items-center">
                <h6 className="mb-0 me-auto fw-semibold">Breakdown</h6>
                <div style={{ minWidth: 170 }}>
                  <SelectMenu className="mb-0" icon="ph-rows" value={breakdownBy} onChange={(v) => setBreakdownBy(v as Dim)} options={dimOptions} />
                </div>
              </div>
              <div className="table-responsive">
                <table className="table mb-0">
                  <thead>
                    <tr>
                      <th>{DIMS.find((d) => d.value === breakdownBy)?.label}</th>
                      <th className="text-end">Duration</th>
                      <th className="text-end">Duration %</th>
                      <th className="text-end">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {breakdownRows.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center text-muted py-4">No time entries for this range.</td>
                      </tr>
                    ) : (
                      breakdownRows.map((r) => (
                        <tr key={r.label}>
                          <td className="fw-semibold">{r.label}</td>
                          <td className="text-end font-monospace">{clock(r.hours)}</td>
                          <td className="text-end">{r.pct.toFixed(0)}%</td>
                          <td className="text-end">{r.amount > 0 ? money(r.amount) : <span className="text-muted">–</span>}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  <tfoot className="border-top">
                    <tr>
                      <th>Total</th>
                      <th className="text-end font-monospace">{clock(totalHours)}</th>
                      <th className="text-end">{totalHours ? '100%' : '0%'}</th>
                      <th className="text-end">{totalAmount > 0 ? money(totalAmount) : <span className="text-muted">–</span>}</th>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </AppChrome>
  );
}

function SummaryStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="col-md-3 border-end-md mb-3 mb-md-0">
      <div className="text-muted">{label}</div>
      <h4 className="fw-bold mb-0">{value}</h4>
    </div>
  );
}
